import { trackError } from '@billy/track-error'
import Cookies from 'js-cookie'

import { getAccessTokenCookieName } from '../config/authCookies'
import { ApiContentType } from '../enums/apiContentType'

interface ResponseMeta {
  meta: {
    statusCode: number
    success: boolean
    time: number
  }
}

interface ResponseBody<T> {
  [key: string]: T[]
}

export type APIResponse<T> = ResponseBody<T> & ResponseMeta

export const apiUrl = window.ENV ? window.ENV.newApiUrl : process.env.REACT_APP_API_URL

export function getAccessToken(): string | undefined {
  return Cookies.get(getAccessTokenCookieName())
}

export function setAccessToken(token: string) {
  Cookies.set(getAccessTokenCookieName(), token)
}

const formatStatusMessage = (statusMessage: string) => {
  return statusMessage ? ` - ${statusMessage}` : ''
}

export class APIError extends Error {
  public statusCode: number
  public statusMessage: string
  public body: any
  public validationErrors: any
  public type: string
  public silent = false

  public constructor(statusCode: number, statusMessage: string, body: any, url: string) {
    const statusCodeAndMessage = `${statusCode}${formatStatusMessage(statusMessage)}`
    super(statusCodeAndMessage)

    this.name = this.constructor.name
    this.statusCode = statusCode
    this.statusMessage = statusMessage
    this.body = body
    this.type = body?.errors?.[0]?.code || 'API_ERROR'

    if (body?.errors?.[0]?.detail) {
      const bodyErrors = body.errors.map((error: any) => error.detail).join(', ')
      this.message = `${statusCodeAndMessage} - ${bodyErrors}`
    } else if (body?.message) {
      this.message = `${statusCodeAndMessage} - ${body?.message}`
    } else if (body?.errorMessage) {
      this.message = `${statusCodeAndMessage} - ${body?.errorMessage}`
    } else {
      this.message = `${statusCodeAndMessage} - url: ${url}`
    }

    this.validationErrors = body?.validationErrors
  }
}

export async function getUnAuthRequest(endpoint: string, init?: RequestInit): Promise<any | false> {
  const url = `${apiUrl}${endpoint}`
  const res = await fetch(url, init)

  try {
    return await res.json()
  } catch (err) {
    trackError(err)
    return false
  }
}

export type RequestDataOptions = {
  returnRawResponse?: boolean
  withAuth?: boolean
  skipContentType?: boolean
  contentType?: string
}

type Options = {
  headers?: ApiHeaders
}

export type ApiHeaders = {
  [key: string]: string | number | boolean | undefined
  'X-Filename'?: string
  'X-File-Size'?: string
  'X-Thumbnail-Names'?: string
  'X-Should-Scan'?: boolean
  'X-OrganizationId'?: string
}

export type ApiOptions = Options

export async function request(
  endpoint: string,
  requestOptions: RequestInit = {},
  options: RequestDataOptions = {},
): Promise<any> {
  const { returnRawResponse, withAuth, skipContentType, contentType } = options

  const contentAndAcceptType = contentType || ApiContentType.ApplicationJSON
  const headers = new Headers({
    Accept: contentAndAcceptType,
    ...requestOptions.headers,
  })

  if (!skipContentType) {
    // This is a solution for the issue with passing FormData data format
    // https://stackoverflow.com/questions/49692745/express-using-multer-error-multipart-boundary-not-found-request-sent-by-pos
    headers.append('Content-Type', contentAndAcceptType)
  }

  if (withAuth) {
    const accessToken = getAccessToken()

    if (!accessToken) {
      return
    }

    headers.append('x-access-token', accessToken)
    headers.append('Authorization', `Bearer ${accessToken}`)
  }

  // @TODO we need to figure out how to add microservices - not just absolute URLs
  const res = await fetch(`${endpoint.startsWith('http') || endpoint.startsWith('https') ? '' : apiUrl}${endpoint}`, {
    mode: 'cors',
    ...requestOptions,
    headers,
  })

  if (!res.ok) {
    let body

    try {
      // We do not read the response directly with json() because the error thrown is not catchable.
      // Moreover, we are only interested in the body if it is a valid JSON object
      body = JSON.parse(await res.text())
    } catch {
      // If we can't read the body, we simply don't pass it
    }

    if (res.status === 404 && body?.errorMessage?.startsWith('No `organization` record with id')) {
      window.location.href = '/logout'
      return
    }

    throw new APIError(res.status, res.statusText, body, res.url)
  }

  if (returnRawResponse) {
    return res
  }

  if (res.status === 204) {
    return
  }

  if (res.status === 201) {
    try {
      return await res.json()
    } catch {
      return undefined
    }
  }

  const responseBody = await res.text()

  try {
    return JSON.parse(responseBody)
  } catch {
    const contentType = res.headers.get('content-type') ?? 'unknown'
    const endpointPathWithoutQuery = res.url.split('?')[0]

    throw new APIError(
      res.status,
      `Non-JSON response body (${contentType})`,
      { errorMessage: `Failed to parse response body as JSON. Endpoint: ${endpointPathWithoutQuery}` },
      res.url,
    )
  }
}

export function getRawRequest(endpoint: string, headers = {}, withAuth = true): Promise<Response> {
  return request(endpoint, { headers }, { returnRawResponse: true, withAuth })
}

export function getRequest(endpoint: string, headers = {}, withAuth = true): Promise<any> {
  return request(endpoint, { headers }, { returnRawResponse: false, withAuth })
}

export function postRequest(endpoint: string, data: any, headers = {}, withAuth = true): Promise<any> {
  return request(
    endpoint,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    },
    {
      returnRawResponse: false,
      withAuth,
    },
  )
}

interface PostV2Args<T> {
  endpoint: string
  headers?: Headers
  options?: RequestDataOptions
  payload?: T
}

// TODO: Replace old usages of old `postRequest` and rename new one with the old one name
export function postV2Request<T, K = any>({ endpoint, headers, options = {}, payload }: PostV2Args<T>): Promise<K> {
  return request(
    endpoint,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    },
    {
      returnRawResponse: false,
      withAuth: true,
      ...options,
    },
  )
}

export function postFileRequest(endpoint: string, file: File, options: Options): Promise<any> {
  const defaultHeaders = {
    'X-Filename': encodeURI(file.name),
    'X-File-Size': file.size,
    'X-Thumbnail-Names': 'webapp',
  }

  return request(
    endpoint,
    {
      method: 'POST',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      headers: { ...defaultHeaders, ...options?.headers },
      body: file,
    },
    {
      returnRawResponse: false,
      withAuth: true,
      skipContentType: true,
    },
  )
}

interface PostFormFileArgs {
  endpoint: string
  fields: Record<string, string>
  file: File
  options?: Options
  signal?: AbortSignal
}

export function postFormFileRequest(args: PostFormFileArgs): Promise<any> {
  const { endpoint, fields, file, options = {}, signal } = args
  const formData = new FormData()
  formData.append('files', file)
  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value)
  }

  return request(
    endpoint,
    {
      method: 'POST',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      headers: { ...options?.headers },
      body: formData,
      signal,
    },
    {
      returnRawResponse: false,
      withAuth: true,
      skipContentType: true,
    },
  )
}

export function putRequest(endpoint: string, data: any, headers = {}, withAuth = true): Promise<any> {
  return request(
    endpoint,
    {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    },
    {
      returnRawResponse: false,
      withAuth,
    },
  )
}

interface PutV2Args<T> {
  endpoint: string
  headers?: Headers
  options?: RequestDataOptions
  payload?: T
}

// TODO: Replace old usages of  old `putRequest` and rename new one with the old one name
export function putV2Request<T, K = any>({ endpoint, headers, options = {}, payload }: PutV2Args<T>): Promise<K> {
  return request(
    endpoint,
    {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload),
    },
    {
      returnRawResponse: false,
      withAuth: true,
      ...options,
    },
  )
}

export function patchRequest(
  endpoint: string,
  data: any,
  headers = {},
  withAuth = true,
  contentType?: ApiContentType,
): Promise<any> {
  return request(
    endpoint,
    {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
    },
    {
      returnRawResponse: false,
      withAuth,
      contentType,
    },
  )
}

export function deleteRequest(endpoint: string, headers = {}, withAuth = true): Promise<any> {
  return request(
    endpoint,
    {
      method: 'DELETE',
      headers,
    },
    {
      returnRawResponse: true,
      withAuth,
    },
  )
}

interface DeleteV2Args {
  endpoint: string
  headers?: Headers
  options?: RequestDataOptions
}

// TODO: Replace old usages of  old `putRequest` and rename new one with the old one name
export function deleteV2Request<T = any>({ endpoint, headers, options = {} }: DeleteV2Args): Promise<T> {
  return request(
    endpoint,
    {
      method: 'DELETE',
      headers,
    },
    {
      returnRawResponse: false,
      withAuth: true,
      ...options,
    },
  )
}

export const patchRequestWithSetContentType = (endpoint: string, data: any) => {
  return request(
    endpoint,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
    {
      withAuth: true,
      returnRawResponse: false,
      skipContentType: true,
      contentType: ApiContentType.ApplicationBillyJSON,
    },
  )
}
